import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@agms/ui'
import {
  merchantMaintenanceApi,
  type MerchantMaintenanceFeaturePayload,
  type MerchantMaintenanceProfileField,
  type MerchantMaintenanceProfilePayload,
  type MerchantMaintenanceProgrammingPayload,
} from '../../lib/api'
import { usePermissions } from '../../hooks/useAuth'

interface MerchantMaintenanceSectionProps {
  merchant: {
    id: string
    dba_name: string
    fiserv_mid: string | null
  }
}

interface EditableProfileField {
  field: MerchantMaintenanceProfileField
  fiservKey: string
  label: string
  inputType?: string
  maxLength?: number
}

const PROFILE_FIELDS: EditableProfileField[] = [
  { field: 'business_email', fiservKey: 'businessEmail', label: 'Business email', inputType: 'email', maxLength: 255 },
  { field: 'business_phone', fiservKey: 'businessPhone', label: 'Business phone', inputType: 'tel', maxLength: 30 },
  { field: 'contact_name', fiservKey: 'contactName', label: 'Contact name', maxLength: 255 },
  { field: 'mailing_address', fiservKey: 'mailingAddressLine1', label: 'Mailing address', maxLength: 255 },
  { field: 'mailing_city', fiservKey: 'mailingCity', label: 'Mailing city', maxLength: 100 },
  { field: 'mailing_state', fiservKey: 'mailingState', label: 'Mailing state', maxLength: 2 },
  { field: 'mailing_zip', fiservKey: 'mailingZip', label: 'Mailing ZIP', maxLength: 10 },
]

// The Reporting profile response nests values inside sections (MerchantInformation,
// BusinessInformation1, ...) whose key names differ from our Common API update keys.
// Candidates are checked in order against a case-insensitive walk of the response.
const PROFILE_VALUE_CANDIDATES: Record<MerchantMaintenanceProfileField, string[]> = {
  business_email: ['businessemail', 'emailaddress', 'email'],
  business_phone: ['businessphone', 'businessphonenumber', 'phonenumber', 'phone'],
  contact_name: ['contactname', 'attention', 'corporateattention'],
  mailing_address: ['mailingaddressline1', 'mailingaddress1', 'address1'],
  mailing_city: ['mailingcity', 'city'],
  mailing_state: ['mailingstate', 'state'],
  mailing_zip: ['mailingzip', 'zipcode', 'zip'],
}

const FEATURE_FIELDS: Array<{
  field: keyof MerchantMaintenanceFeaturePayload
  label: string
  options: Array<{ value: string; label: string }>
}> = [
  { field: 'tipPrompt', label: 'Tip prompt', options: yesNoOptions() },
  { field: 'cashBack', label: 'Cash back', options: yesNoOptions() },
  { field: 'signatureRequired', label: 'Signature required', options: yesNoOptions() },
  {
    field: 'receiptPrintMode',
    label: 'Receipt print mode',
    options: [
      { value: '', label: 'No change' },
      { value: 'merchant', label: 'Merchant' },
      { value: 'customer', label: 'Customer' },
      { value: 'both', label: 'Both' },
    ],
  },
]

const PROGRAMMING_FIELDS: Array<{
  field: keyof MerchantMaintenanceProgrammingPayload
  label: string
  maxLength: number
}> = [
  { field: 'downloadPhone', label: 'Download phone', maxLength: 30 },
  { field: 'terminalIdNumber', label: 'Terminal ID number', maxLength: 16 },
]

function yesNoOptions() {
  return [
    { value: '', label: 'No change' },
    { value: 'Y', label: 'Yes' },
    { value: 'N', label: 'No' },
  ]
}

function valueToString(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// Some Reporting responses deliver nested sections as JSON-encoded strings.
function maybeParseJson(value: unknown): unknown {
  if (typeof value !== 'string') return value
  const trimmed = value.trim()
  const looksJson = (trimmed.startsWith('{') && trimmed.endsWith('}'))
    || (trimmed.startsWith('[') && trimmed.endsWith(']'))
  if (!looksJson) return value
  try {
    return JSON.parse(trimmed)
  } catch {
    return value
  }
}

// "MerchantNumber" -> "Merchant Number", "DDANumber" -> "DDA Number",
// "BusinessInformation1" -> "Business Information 1"
function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Fiserv encodes many values as "<code> - <description>".
function splitCodedValue(value: string): { code: string; description: string } | null {
  const match = value.match(/^([A-Za-z0-9./]{1,8})\s+-\s+(\S[\s\S]{2,})$/)
  if (!match) return null
  return { code: match[1], description: match[2].replace(/\s+/g, ' ').trim() }
}

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function findNestedValue(data: unknown, candidate: string): string | null {
  if (Array.isArray(data)) {
    for (const item of data) {
      const found = findNestedValue(item, candidate)
      if (found !== null) return found
    }
    return null
  }
  if (!isPlainObject(data)) return null
  for (const [key, rawValue] of Object.entries(data)) {
    const value = maybeParseJson(rawValue)
    if (normalizeKey(key) === candidate && (typeof value === 'string' || typeof value === 'number')) {
      const text = String(value).trim()
      if (text !== '') return text
    }
  }
  for (const rawValue of Object.values(data)) {
    const value = maybeParseJson(rawValue)
    if (isPlainObject(value) || Array.isArray(value)) {
      const found = findNestedValue(value, candidate)
      if (found !== null) return found
    }
  }
  return null
}

function fieldValue(profile: Record<string, unknown>, field: EditableProfileField): string {
  const flat = profile[field.fiservKey] ?? profile[field.field]
  if (flat !== null && flat !== undefined && flat !== '') return valueToString(flat)
  for (const candidate of PROFILE_VALUE_CANDIDATES[field.field] ?? []) {
    const found = findNestedValue(profile, candidate)
    if (found !== null) return found
  }
  return '—'
}

function countLeafFields(value: unknown): number {
  const parsed = maybeParseJson(value)
  if (Array.isArray(parsed)) return parsed.reduce((sum: number, item) => sum + countLeafFields(item), 0)
  if (isPlainObject(parsed)) return Object.values(parsed).reduce((sum: number, item) => sum + countLeafFields(item), 0)
  return 1
}

function matchesQuery(key: string, value: unknown, query: string): boolean {
  if (!query) return true
  if (humanizeKey(key).toLowerCase().includes(query) || key.toLowerCase().includes(query)) return true
  return JSON.stringify(value ?? '').toLowerCase().includes(query)
}

function ValueDisplay({ value }: { value: unknown }) {
  const text = valueToString(value)
  if (text === '—') return <span className="text-gray-400">—</span>
  const coded = typeof value === 'string' ? splitCodedValue(value) : null
  if (coded) {
    return (
      <span>
        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-800">{coded.code}</code>
        <span className="ml-2 text-gray-600">{coded.description}</span>
      </span>
    )
  }
  return <span className="break-words">{text}</span>
}

function FieldGrid({ data, query }: { data: Record<string, unknown>; query: string }) {
  const scalarEntries: Array<[string, unknown]> = []
  const nestedEntries: Array<[string, unknown]> = []
  for (const [key, rawValue] of Object.entries(data)) {
    const value = maybeParseJson(rawValue)
    if (isPlainObject(value) || Array.isArray(value)) {
      nestedEntries.push([key, value])
    } else {
      scalarEntries.push([key, value])
    }
  }

  const visibleScalars = scalarEntries.filter(([key, value]) => matchesQuery(key, value, query))
  const visibleNested = nestedEntries.filter(([key, value]) => matchesQuery(key, value, query))

  return (
    <div className="space-y-4">
      {visibleScalars.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2 xl:grid-cols-3">
          {visibleScalars.map(([key, value]) => (
            <div key={key} className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{humanizeKey(key)}</dt>
              <dd className="mt-0.5 text-sm text-gray-900">
                <ValueDisplay value={value} />
              </dd>
            </div>
          ))}
        </dl>
      )}
      {visibleNested.map(([key, value]) => (
        <div key={key} className="rounded-md border border-gray-100 bg-gray-50/60 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{humanizeKey(key)}</p>
          <NestedValue value={value} query={query} />
        </div>
      ))}
    </div>
  )
}

function NestedValue({ value, query }: { value: unknown; query: string }) {
  if (Array.isArray(value)) {
    if (value.length === 0) return <p className="text-sm text-gray-400">No entries.</p>
    return (
      <div className="space-y-3">
        {value.map((item, index) => {
          const parsed = maybeParseJson(item)
          return (
            <div key={index} className="rounded-md border border-gray-200 bg-white p-3">
              {value.length > 1 && (
                <p className="mb-2 text-xs font-semibold text-gray-400">Entry {index + 1}</p>
              )}
              <NestedValue value={parsed} query={query} />
            </div>
          )
        })}
      </div>
    )
  }
  if (isPlainObject(value)) return <FieldGrid data={value} query={query} />
  return (
    <p className="text-sm text-gray-900">
      <ValueDisplay value={value} />
    </p>
  )
}

interface ProfileSectionDef {
  key: string
  title: string
  value: unknown
}

function ProfileSectionCard({
  section,
  isOpen,
  onToggle,
  query,
}: {
  section: ProfileSectionDef
  isOpen: boolean
  onToggle: () => void
  query: string
}) {
  const fieldCount = useMemo(() => countLeafFields(section.value), [section.value])
  const open = query !== '' || isOpen

  return (
    <div className="overflow-hidden rounded-md border border-gray-200">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 bg-gray-50 px-4 py-2.5 text-left hover:bg-gray-100"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">{section.title}</span>
          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
            {fieldCount} {fieldCount === 1 ? 'field' : 'fields'}
          </span>
        </span>
        <svg
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open && (
        <div className="border-t border-gray-200 bg-white px-4 py-3">
          <NestedValue value={section.value} query={query} />
        </div>
      )}
    </div>
  )
}

function terminalIdFor(row: Record<string, unknown>): string {
  return String(row.terminalId ?? row.terminalID ?? row.terminal_id ?? row.id ?? 'unknown-terminal')
}

function modelFor(row: Record<string, unknown>): string {
  return valueToString(row.model ?? row.terminalModel ?? row.equipmentModel ?? row.makeModel)
}

function isTerminalStatus(status: string): boolean {
  return ['completed', 'success', 'failed', 'error'].includes(status.toLowerCase())
}

function statusClass(status: string): string {
  const normalized = status.toLowerCase()
  if (['completed', 'success'].includes(normalized)) return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  if (['failed', 'error'].includes(normalized)) return 'border-rose-200 bg-rose-50 text-rose-700'
  return 'border-blue-200 bg-blue-50 text-blue-700'
}

function errorMessage(error: unknown): string {
  const maybe = error as { response?: { data?: { message?: string; errorMessage?: string; error_code?: string } } }
  if (maybe.response?.data?.error_code === 'fiserv_role_not_provisioned') {
    return maybe.response.data.message ?? 'Fiserv Common API credential lacks AccountMaintenanceAPI role.'
  }
  return maybe.response?.data?.errorMessage
    ?? maybe.response?.data?.message
    ?? 'Maintenance unavailable. Refresh to retry.'
}

function isRoleNotProvisioned(error: unknown): boolean {
  const maybe = error as { response?: { data?: { error_code?: string } } }
  return maybe.response?.data?.error_code === 'fiserv_role_not_provisioned'
}

export function MerchantMaintenanceSection({ merchant }: MerchantMaintenanceSectionProps) {
  const { canEditMerchantMaintenance } = usePermissions()
  const canEdit = canEditMerchantMaintenance()
  const [profile, setProfile] = useState<Record<string, unknown> | null>(null)
  const [profileFetchedAt, setProfileFetchedAt] = useState<string | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [profileRoleUnavailable, setProfileRoleUnavailable] = useState(false)
  const [profileQuery, setProfileQuery] = useState('')
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const [equipment, setEquipment] = useState<Record<string, unknown>[] | null>(null)
  const [equipmentError, setEquipmentError] = useState<string | null>(null)
  const [expandedTerminals, setExpandedTerminals] = useState<Record<string, boolean>>({})
  const [editingProfile, setEditingProfile] = useState<EditableProfileField | null>(null)
  const [profileValue, setProfileValue] = useState('')
  const [equipmentModal, setEquipmentModal] = useState<{
    type: 'feature' | 'programming'
    row: Record<string, unknown>
  } | null>(null)
  const [featureForm, setFeatureForm] = useState<Record<string, string>>({})
  const [programmingForm, setProgrammingForm] = useState<Record<string, string>>({})
  const [confirmAction, setConfirmAction] = useState<{
    title: string
    body: string
    onConfirm: () => void
  } | null>(null)
  const [activeRequest, setActiveRequest] = useState<{ id: string; label: string; startedAt: number } | null>(null)
  const [lastRequest, setLastRequest] = useState<{ id: string; label: string } | null>(null)
  const [requestStatus, setRequestStatus] = useState<{ status: string; message: string } | null>(null)

  const profileSections = useMemo<ProfileSectionDef[]>(() => {
    if (!profile) return []
    const sections: ProfileSectionDef[] = []
    const general: Record<string, unknown> = {}
    for (const [key, rawValue] of Object.entries(profile)) {
      const value = maybeParseJson(rawValue)
      if (isPlainObject(value) || Array.isArray(value)) {
        sections.push({ key, title: humanizeKey(key), value })
      } else {
        general[key] = value
      }
    }
    if (Object.keys(general).length > 0) {
      sections.unshift({ key: '__general__', title: 'General', value: general })
    }
    return sections
  }, [profile])

  const normalizedQuery = profileQuery.trim().toLowerCase()
  const visibleSections = useMemo(() => {
    if (!normalizedQuery) return profileSections
    return profileSections.filter(
      (section) =>
        section.title.toLowerCase().includes(normalizedQuery)
        || matchesQuery(section.key, section.value, normalizedQuery),
    )
  }, [profileSections, normalizedQuery])

  const fetchProfile = useMutation({
    mutationFn: () => merchantMaintenanceApi.getProfile(merchant.id),
    onSuccess: (data) => {
      setProfile(data.profile)
      setProfileFetchedAt(new Date().toLocaleString())
      setProfileError(null)
      setProfileRoleUnavailable(false)
      setProfileQuery('')
      // Open the first section so the fetch visibly lands; the rest stay collapsed.
      const firstNested = Object.entries(data.profile).find(([, value]) => {
        const parsed = maybeParseJson(value)
        return isPlainObject(parsed) || Array.isArray(parsed)
      })
      setExpandedSections(firstNested ? { [firstNested[0]]: true } : {})
    },
    onError: (error) => {
      setProfileError(errorMessage(error))
      setProfileRoleUnavailable(isRoleNotProvisioned(error))
    },
  })

  const fetchEquipment = useMutation({
    mutationFn: () => merchantMaintenanceApi.listEquipment(merchant.id),
    onSuccess: (data) => {
      setEquipment(data.equipment)
      setEquipmentError(null)
      setExpandedTerminals({})
    },
    onError: (error) => {
      setEquipmentError(errorMessage(error))
    },
  })

  const updateProfile = useMutation({
    mutationFn: (payload: MerchantMaintenanceProfilePayload) => merchantMaintenanceApi.updateProfile(merchant.id, payload),
    onSuccess: (data) => {
      setEditingProfile(null)
      if (data.requestId) {
        startPolling(data.requestId, 'Profile update')
      }
    },
    onError: (error) => {
      setProfileError(errorMessage(error))
    },
  })

  const updateFeature = useMutation({
    mutationFn: ({ terminalId, payload }: { terminalId: string; payload: MerchantMaintenanceFeaturePayload }) =>
      merchantMaintenanceApi.updateEquipmentFeature(merchant.id, terminalId, payload),
    onSuccess: (data) => {
      setEquipmentModal(null)
      if (data.requestId) startPolling(data.requestId, 'Equipment feature update')
    },
    onError: (error) => setEquipmentError(errorMessage(error)),
  })

  const updateProgramming = useMutation({
    mutationFn: ({ terminalId, payload }: { terminalId: string; payload: MerchantMaintenanceProgrammingPayload }) =>
      merchantMaintenanceApi.updateEquipmentProgramming(merchant.id, terminalId, payload),
    onSuccess: (data) => {
      setEquipmentModal(null)
      if (data.requestId) startPolling(data.requestId, 'Equipment programming update')
    },
    onError: (error) => setEquipmentError(errorMessage(error)),
  })

  useEffect(() => {
    if (!activeRequest) return

    const poll = async () => {
      try {
        const result = await merchantMaintenanceApi.checkRequestStatus(merchant.id, activeRequest.id)
        const status = result.status || 'unknown'
        const rawMessage = valueToString(result.rawResponse.message ?? result.rawResponse.error ?? result.rawResponse.responseMessage)

        if (['completed', 'success'].includes(status.toLowerCase())) {
          setRequestStatus({ status, message: 'Update applied.' })
          setActiveRequest(null)
          return
        }

        if (['failed', 'error'].includes(status.toLowerCase())) {
          setRequestStatus({ status, message: `Fiserv rejected the update: ${rawMessage}` })
          setActiveRequest(null)
          return
        }

        if (Date.now() - activeRequest.startedAt >= 60_000) {
          setRequestStatus({ status, message: 'Update is still pending. Check back later.' })
          setActiveRequest(null)
          return
        }

        setRequestStatus({ status, message: 'Update queued at Fiserv...' })
      } catch {
        setRequestStatus({ status: 'unknown', message: 'Unable to refresh update status.' })
      }
    }

    poll()
    const interval = window.setInterval(poll, 5000)
    return () => window.clearInterval(interval)
  }, [activeRequest, merchant.id])

  const startPolling = (requestId: string, label: string) => {
    setRequestStatus({ status: 'pending', message: 'Update queued at Fiserv...' })
    setLastRequest({ id: requestId, label })
    setActiveRequest({ id: requestId, label, startedAt: Date.now() })
  }

  const beginProfileEdit = (field: EditableProfileField) => {
    setEditingProfile(field)
    const current = profile ? fieldValue(profile, field) : ''
    setProfileValue(current === '—' ? '' : current)
  }

  const submitProfileEdit = () => {
    if (!editingProfile) return
    const payload = { [editingProfile.field]: profileValue } as MerchantMaintenanceProfilePayload
    setConfirmAction({
      title: `Update ${editingProfile.label} for ${merchant.dba_name}?`,
      body: `This will submit a real change to Fiserv for ${merchant.dba_name}.`,
      onConfirm: () => updateProfile.mutate(payload),
    })
  }

  const openEquipmentModal = (type: 'feature' | 'programming', row: Record<string, unknown>) => {
    setEquipmentModal({ type, row })
    setFeatureForm({})
    setProgrammingForm({})
  }

  const submitEquipment = () => {
    if (!equipmentModal) return
    const terminalId = terminalIdFor(equipmentModal.row)
    const isFeature = equipmentModal.type === 'feature'
    const payload = Object.fromEntries(
      Object.entries(isFeature ? featureForm : programmingForm).filter(([, value]) => value !== ''),
    )

    setConfirmAction({
      title: `Update ${isFeature ? 'features' : 'programming'} for ${merchant.dba_name}?`,
      body: `This will submit a real ${isFeature ? 'feature' : 'programming'} change to Fiserv for terminal ${terminalId}.`,
      onConfirm: () => {
        if (isFeature) {
          updateFeature.mutate({ terminalId, payload: payload as MerchantMaintenanceFeaturePayload })
        } else {
          updateProgramming.mutate({ terminalId, payload: payload as MerchantMaintenanceProgrammingPayload })
        }
      },
    })
  }

  const setAllSections = (open: boolean) => {
    setExpandedSections(Object.fromEntries(profileSections.map((section) => [section.key, open])))
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Fiserv Profile</CardTitle>
              <p className="mt-1 text-sm text-gray-500">Fetch profile data on demand from Fiserv Reporting API.</p>
            </div>
            <div className="flex items-center gap-3">
              {profileFetchedAt && <span className="text-xs text-gray-400">Fetched {profileFetchedAt}</span>}
              <Button onClick={() => fetchProfile.mutate()} disabled={fetchProfile.isPending}>
                {fetchProfile.isPending ? 'Fetching...' : profile ? 'Refresh from Fiserv' : 'Fetch from Fiserv'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {profileError && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <p className="font-semibold">Maintenance unavailable</p>
              <p>{profileError}</p>
              {profileRoleUnavailable && (
                <p className="mt-1 text-xs">More info: Fiserv must enable AccountMaintenanceAPI before live profile reads will work.</p>
              )}
            </div>
          )}
          {requestStatus && (
            <div className={`mb-4 rounded-md border px-3 py-2 text-sm ${statusClass(requestStatus.status)}`}>
              <span className="font-semibold">{activeRequest?.label ?? 'Latest update'}: </span>{requestStatus.message}
              {!activeRequest && lastRequest && !isTerminalStatus(requestStatus.status) && (
                <Button variant="secondary" className="ml-3" onClick={() => startPolling(lastRequest.id, lastRequest.label)}>
                  Refresh
                </Button>
              )}
            </div>
          )}
          {fetchProfile.isPending && !profile && (
            <div className="space-y-3 py-2" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-10 animate-pulse rounded-md bg-gray-100" />
              ))}
            </div>
          )}
          {!profile && !profileError && !fetchProfile.isPending && (
            <div className="rounded-md border border-dashed border-gray-200 px-4 py-8 text-center">
              <p className="text-sm font-medium text-gray-700">No profile loaded yet</p>
              <p className="mt-1 text-sm text-gray-500">Click Fetch from Fiserv when you need current portfolio data.</p>
            </div>
          )}
          {profile && (
            <div className="space-y-5">
              <div className="rounded-md border border-gray-200">
                <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Editable fields
                </div>
                <div className="grid grid-cols-1 divide-y divide-gray-100 lg:grid-cols-2 lg:divide-y-0 lg:[&>*:nth-child(n+3)]:border-t lg:[&>*:nth-child(even)]:border-l lg:[&>*]:border-gray-100">
                  {PROFILE_FIELDS.map((field) => (
                    <div key={field.field} className="flex items-center justify-between gap-2 px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{field.label}</p>
                        <p className="truncate text-sm text-gray-900">{fieldValue(profile, field)}</p>
                      </div>
                      {canEdit && (
                        <Button variant="secondary" onClick={() => beginProfileEdit(field)}>
                          Edit
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <Input
                    aria-label="Search Fiserv profile fields"
                    placeholder="Search profile fields..."
                    value={profileQuery}
                    onChange={(event) => setProfileQuery(event.target.value)}
                    className="w-full max-w-xs"
                  />
                  <div className="flex gap-2 text-xs">
                    <button type="button" className="font-medium text-blue-600 hover:text-blue-700" onClick={() => setAllSections(true)}>
                      Expand all
                    </button>
                    <span className="text-gray-300">|</span>
                    <button type="button" className="font-medium text-blue-600 hover:text-blue-700" onClick={() => setAllSections(false)}>
                      Collapse all
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  {visibleSections.map((section) => (
                    <ProfileSectionCard
                      key={section.key}
                      section={section}
                      query={normalizedQuery}
                      isOpen={expandedSections[section.key] ?? false}
                      onToggle={() =>
                        setExpandedSections((current) => ({ ...current, [section.key]: !(current[section.key] ?? false) }))
                      }
                    />
                  ))}
                  {visibleSections.length === 0 && (
                    <p className="py-4 text-center text-sm text-gray-500">No fields match “{profileQuery}”.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Equipment</CardTitle>
              <p className="mt-1 text-sm text-gray-500">Refresh equipment from Fiserv before submitting feature or programming changes.</p>
            </div>
            <Button onClick={() => fetchEquipment.mutate()} disabled={fetchEquipment.isPending}>
              {fetchEquipment.isPending ? 'Refreshing...' : 'Refresh Equipment List'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {equipmentError && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {equipmentError}
            </div>
          )}
          {fetchEquipment.isPending && !equipment && (
            <div className="space-y-3 py-2" aria-hidden="true">
              {[0, 1].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-md bg-gray-100" />
              ))}
            </div>
          )}
          {!equipment && !equipmentError && !fetchEquipment.isPending && (
            <div className="rounded-md border border-dashed border-gray-200 px-4 py-8 text-center">
              <p className="text-sm font-medium text-gray-700">No equipment loaded yet</p>
              <p className="mt-1 text-sm text-gray-500">Refresh the equipment list to see terminals on file with Fiserv.</p>
            </div>
          )}
          {equipment && equipment.length === 0 && (
            <p className="py-4 text-sm text-gray-500">Fiserv returned no equipment rows for this merchant.</p>
          )}
          {equipment && equipment.length > 0 && (
            <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
              {equipment.map((row) => {
                const terminalId = terminalIdFor(row)
                const detailsOpen = expandedTerminals[terminalId] ?? false
                return (
                  <div key={terminalId} className="px-3 py-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="min-w-0">
                        <p className="font-mono text-sm font-semibold text-gray-900">{terminalId}</p>
                        <p className="text-sm text-gray-600">Model: {modelFor(row)}</p>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <Button
                          variant="secondary"
                          onClick={() =>
                            setExpandedTerminals((current) => ({ ...current, [terminalId]: !detailsOpen }))
                          }
                        >
                          {detailsOpen ? 'Hide Details' : 'Details'}
                        </Button>
                        {canEdit && (
                          <>
                            <Button variant="secondary" onClick={() => openEquipmentModal('feature', row)}>
                              Update Features
                            </Button>
                            <Button variant="secondary" onClick={() => openEquipmentModal('programming', row)}>
                              Update Programming
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    {detailsOpen && (
                      <div className="mt-3 rounded-md border border-gray-100 bg-gray-50/60 p-3">
                        <FieldGrid data={row} query="" />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {editingProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Edit {editingProfile.label}</h2>
            </div>
            <div className="space-y-3 px-5 py-4">
              <Input
                aria-label={editingProfile.label}
                type={editingProfile.inputType ?? 'text'}
                value={profileValue}
                maxLength={editingProfile.maxLength}
                onChange={(event) => setProfileValue(event.target.value)}
              />
              <p className="text-xs text-gray-500">Saving opens a confirmation before anything is sent to Fiserv.</p>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <Button variant="secondary" onClick={() => setEditingProfile(null)}>Cancel</Button>
              <Button onClick={submitProfileEdit}>Save</Button>
            </div>
          </div>
        </div>
      )}

      {equipmentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Update {equipmentModal.type === 'feature' ? 'Features' : 'Programming'}
              </h2>
              <p className="mt-1 text-sm text-gray-500">Terminal {terminalIdFor(equipmentModal.row)}</p>
            </div>
            <div className="space-y-4 px-5 py-4">
              {equipmentModal.type === 'feature' ? (
                FEATURE_FIELDS.map((field) => (
                  <label key={field.field} className="block text-sm font-medium text-gray-700">
                    {field.label}
                    <select
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={featureForm[field.field] ?? ''}
                      onChange={(event) => setFeatureForm((current) => ({ ...current, [field.field]: event.target.value }))}
                    >
                      {field.options.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                ))
              ) : (
                PROGRAMMING_FIELDS.map((field) => (
                  <label key={field.field} className="block text-sm font-medium text-gray-700">
                    {field.label}
                    <Input
                      className="mt-1"
                      value={programmingForm[field.field] ?? ''}
                      maxLength={field.maxLength}
                      onChange={(event) => setProgrammingForm((current) => ({ ...current, [field.field]: event.target.value }))}
                    />
                  </label>
                ))
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <Button variant="secondary" onClick={() => setEquipmentModal(null)}>Cancel</Button>
              <Button onClick={submitEquipment}>Submit</Button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">{confirmAction.title}</h2>
            </div>
            <div className="space-y-3 px-5 py-4">
              <p className="text-sm text-gray-700">{confirmAction.body}</p>
              <p className="text-sm text-amber-800">Confirming fires a real Account Maintenance request to Fiserv.</p>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <Button variant="secondary" autoFocus onClick={() => setConfirmAction(null)}>Cancel</Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700"
                disabled={updateProfile.isPending || updateFeature.isPending || updateProgramming.isPending}
                onClick={() => {
                  const action = confirmAction.onConfirm
                  setConfirmAction(null)
                  action()
                }}
              >
                Confirm Fiserv Update
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
